// environment.js — canonical analytics environment classification
//
// BL-025 Phase 2 (PO decision, June 4 2026): a SINGLE shared PostHog project
// backs every tier. There is no second PostHog project. Isolation is achieved
// by tagging every analytics event/profile with one of four canonical
// environment values so production dashboards can filter `environment =
// production` and non-production activity stays visible-but-separable.
//
// This module is the single source of truth for that classification. The pure
// classifier (`classifyEnvironment`) is deterministic from its inputs so it
// can be unit-pinned; `getEnvironment` wires it to the build-time override
// (REACT_APP_ENVIRONMENT) and the live hostname.

export const ENVIRONMENTS = Object.freeze({
  PRODUCTION: "production",
  PREVIEW: "preview",
  DEVELOPMENT: "development",
  LOCALHOST: "localhost",
});

const VALID = Object.freeze(Object.values(ENVIRONMENTS));

// Canonical production hostnames (exact match, case-insensitive). Anything we
// cannot positively identify as production must NOT be classified as such —
// keeping unknown traffic out of production analytics is the whole point.
const PRODUCTION_HOSTS = Object.freeze(["rentava.io", "www.rentava.io"]);

export function isValidEnvironment(env) {
  return typeof env === "string" && VALID.includes(env);
}

// Pure classifier. `explicit` (e.g. REACT_APP_ENVIRONMENT) wins when it is a
// valid canonical value; otherwise the environment is inferred from the
// hostname. Unidentified hosts fall back to `development` (never production).
export function classifyEnvironment(hostname, explicit) {
  if (isValidEnvironment(explicit)) return explicit;

  const host = (typeof hostname === "string" ? hostname : "").trim().toLowerCase();
  if (!host) return ENVIRONMENTS.DEVELOPMENT;

  if (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "0.0.0.0" ||
    host === "[::1]" ||
    host.endsWith(".local")
  ) {
    return ENVIRONMENTS.LOCALHOST;
  }

  if (PRODUCTION_HOSTS.includes(host)) return ENVIRONMENTS.PRODUCTION;

  // Netlify deploy-preview / branch-deploy / *.netlify.app builds.
  if (host.endsWith(".netlify.app") || host.includes("deploy-preview-")) {
    return ENVIRONMENTS.PREVIEW;
  }

  return ENVIRONMENTS.DEVELOPMENT;
}

// Runtime resolver — the value analytics tags events with. Optional explicit
// override via REACT_APP_ENVIRONMENT (set per Netlify deploy context if a
// build's hostname is ambiguous); otherwise inferred from window.location.
export function getEnvironment() {
  const explicit =
    typeof process !== "undefined" && process.env
      ? process.env.REACT_APP_ENVIRONMENT
      : undefined;
  const hostname =
    typeof window !== "undefined" && window.location
      ? window.location.hostname
      : "";
  return classifyEnvironment(hostname, explicit);
}
